import { computed, ref } from 'vue';
import { createSharedComposable } from '@vueuse/core';
import { useRouter } from 'vue-router';
import {
  MSE_PRODUCT,
  SEARCH_TYPES,
  productKeyMap,
  searchKeyMap,
  type MseProduct,
} from '@/types/home/product';
import ICON from '@/assets/icon/products';
import { useFindSolutions } from '../find-solutions/use-find-solutions';
import { useConversation } from '@/composables/tech-qa/use-conversation';
import { useUpload } from '../tech-qa/use-upload';
import { getExamples } from './use-examples';
import { useFormulationUpload } from '../formulation/upload/use-upload';
import { useFormulationConversation } from '../formulation/use-conversation';
import { useConversation as useFmeaConversation } from '../fmea/use-conversation';

// OLD PRODUCT ROUTING BEFORE INTEGRATION
import { gotoPlaybook } from '@/components/pages/home/utils/agent-card';
import { injectService } from '@patsnap/tahiti-core';
import { HomeMaterialApi } from '@/domains/home/apis/home-material-api';
import { useFormulationLegacy } from '../formulation/use-formulation-legacy';

export const useMseProducts = createSharedComposable(() => {
  const homeMaterialApiService = injectService<HomeMaterialApi>(HomeMaterialApi);
  const router = useRouter();
  const { submitPipeline: findSolutionSubmit } = useFindSolutions();
  const { createConversation } = useConversation();
  const { openDialog, uploadExampleImage } = useUpload();
  const { getProductExample, getProductFileExample } = getExamples();
  const { openDialog: openFormulationDialog, context_id: contextId } = useFormulationUpload();
  const { createConversationFromLanding: createFormulationConversation, handleExampleClick } =
    useFormulationConversation();
  const { createConversation: createFmeaConversation } = useFmeaConversation();
  const { submitFormulationLegacyPipeline } = useFormulationLegacy();

  const isSubmitting = ref(false);
  const functionIds = ref<string[]>([]);

  const iconMap: Record<MseProduct, string> = {
    FORMULATION: ICON.Formulation,
    FORMULATION_LEGACY: ICON.Formulation,
    TECHQA: ICON.SearchLeft,
    FIND_SOLUTIONS: ICON.FindSolutions,
    QUICK_RESEARCH: ICON.QuickResearch,
    SMART_SEARCH: ICON.SearchLeft,
    DFMEA_ASSISTANT: ICON.DFMEA,
    DOC_ANALYZER: ICON.DocumentAnalyzer,
    INVENTION_DISCLOSURE: ICON.InventionDisclosure,
    IDEA_VALIDATION: ICON.IdeaValidation,
    TECHNOLGY_ROADMAP: ICON.Roadmap,
    ADVANCED: ICON.SearchLeft,
    SUBSTANCE_INQUIRY: ICON.SubstanceInquiry,
    STRUCTURE_SEARCH: ICON.StructureSearch,
    SYNTH_SEARCH: ICON.WorkflowSearch,
    PROFILE_SEARCH: ICON.PropertySearch,
    COMPOSITION_SEARCH: ICON.MetalSearch,
  };

  const landingPageProducts = ref<MseProduct[]>(['TECHQA', 'FORMULATION']);

  const getFunctionIds = async () => {
    if (functionIds.value.length === 0) {
      functionIds.value = (await homeMaterialApiService.getProducts()).map((p) => p.function_id);
    }
  };

  const getUserProducts = async () => {
    await getFunctionIds();
    const userProducts = new Set(
      functionIds.value.map((fid) => productKeyMap[fid]).filter((p) => p !== undefined),
    );

    return MSE_PRODUCT.filter((p) => userProducts.has(p));
  };

  const getUserSearches = async () => {
    await getFunctionIds();
    const userSearches = new Set(
      functionIds.value.map((fid) => searchKeyMap[fid]).filter((p) => p !== undefined),
    );
    return SEARCH_TYPES.filter((p) => userSearches.has(p));
  };

  const productWithUpload = ref<Set<MseProduct>>(
    new Set<MseProduct>(['TECHQA', 'DOC_ANALYZER', 'FORMULATION', 'FORMULATION_LEGACY']),
  );

  const usedProduct = ref<MseProduct>('TECHQA');

  const setUsedProduct = (p: MseProduct) => {
    // OLD PRODUCT ROUTING BEFORE INTEGRATION
    switch (p) {
      case 'QUICK_RESEARCH':
        gotoPlaybook('research', router);
        return;
      case 'TECHNOLGY_ROADMAP':
        gotoPlaybook('roadmap', router);
        return;
      // case 'DFMEA_ASSISTANT':
      //   gotoPlaybook('fmea', router);
      //   return;
      case 'INVENTION_DISCLOSURE':
        gotoPlaybook('ai_patent_disclosure', router);
        return;
      case 'IDEA_VALIDATION':
        gotoPlaybook('novelty_check', router);
        return;
    }

    usedProduct.value = p;
  };

  const shownExamples = computed(() => {
    return getProductExample(usedProduct.value);
  });

  const shownFileExamples = computed(() => {
    return getProductFileExample(usedProduct.value);
  });

  const exampleClickCallback = (text: string, index: number) => {
    switch (usedProduct.value) {
      case 'FORMULATION': {
        handleExampleClick(text, index);
        break;
      }
    }
  };

  const submitInput = async (input: string) => {
    if (!input) {
      return;
    }

    isSubmitting.value = true;

    switch (usedProduct.value) {
      case 'FIND_SOLUTIONS': {
        await findSolutionSubmit(input);
        break;
      }
      case 'TECHQA': {
        await createConversation(input, router);
        break;
      }
      case 'FORMULATION': {
        const contextIdNumber = contextId.value ? Number(contextId.value) : undefined;
        await createFormulationConversation(input, contextIdNumber);
        break;
      }
      case 'FORMULATION_LEGACY': {
        await submitFormulationLegacyPipeline(input);
        break;
      }
      case 'DFMEA_ASSISTANT': {
        await createFmeaConversation(input);
        break;
      }
    }

    isSubmitting.value = false;
  };

  const handleUpload = async () => {
    switch (usedProduct.value) {
      case 'TECHQA': {
        openDialog();
        break;
      }
      case 'FORMULATION': {
        openFormulationDialog();
        break;
      }
    }
  };

  const handleExampleUpload = async (data: Record<string, any>) => {
    switch (usedProduct.value) {
      case 'TECHQA': {
        if (data.imgLink) await uploadExampleImage(data.imgLink);
        break;
      }
    }
  };

  return {
    iconMap,
    landingPageProducts,
    getUserProducts,
    getUserSearches,
    productWithUpload,
    usedProduct,
    setUsedProduct,
    shownExamples,
    shownFileExamples,
    exampleClickCallback,
    submitInput,
    isSubmitting,
    handleUpload,
    handleExampleUpload,
    getFunctionIds,
    functionIds,
  };
});
